// Risk Management Page
// Consolidated tabbed interface for all risk management operations

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RiskPool } from '../../components/risk/RiskPool';
import { RiskDashboard as RiskDashboardComponent } from '../../components/risk/RiskDashboard';
import { RiskAnalytics } from '../../components/risk/RiskAnalytics';
// Removed individual risk components - consolidated into RiskPool with different view modes
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { ViewModal } from '../../components/common/ViewModal';
import { EditModal } from '../../components/common/EditModal';
import { riskRegisterEntity } from '../../config/entities/riskRegisters';
import { createCRUDService } from '../../services/genericCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
import { PlusIcon } from '@heroicons/react/24/outline';
import { QuickRiskCreationModal } from '../../components/risk/QuickRiskCreationModal';
import { RiskRecentActivity } from '../../components/risk/RiskRecentActivity';
import { RiskDeadlines } from '../../components/risk/RiskDeadlines';
import { RiskHeatMap } from '../../components/risk/RiskHeatMap';

export const RiskManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'overview' | 'pool' | 'registers'>('overview');
  const [autoOpenCreate, setAutoOpenCreate] = useState(false);
  const [showQuickRiskModal, setShowQuickRiskModal] = useState(false);

  // Check for query parameters to auto-open create modal
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const createParam = searchParams.get('create');

    if (tabParam === 'overview' || (createParam === 'true' && !tabParam)) {
      setActiveTab('overview');
      if (createParam === 'true') {
        setShowQuickRiskModal(true);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('create');
        window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
      }
    } else if (tabParam === 'pool') {
      setActiveTab('pool');
      if (createParam === 'true') {
        setAutoOpenCreate(true);
        // Clear the query parameter after setting the flag
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('create');
        window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
      }
    }
  }, [searchParams]);
  
  // Modal states for registers
  const [showCreateRegisterModal, setShowCreateRegisterModal] = useState(false);
  const [showEditRegisterModal, setShowEditRegisterModal] = useState(false);
  const [showViewRegisterModal, setShowViewRegisterModal] = useState(false);
  const [editingRegister, setEditingRegister] = useState<any>(null);
  const [viewingRegister, setViewingRegister] = useState<any>(null);
  const [registerLoading, setRegisterLoading] = useState(false);

  // Use Generic CRUD Services
  const riskRegisterService = createCRUDService(riskRegisterEntity);

  const handleBulkAssign = (_riskIds: string[], _ownerId: string) => {
    // Handle bulk assignment - FlexibleCRUD will handle this
  };

  // Register handlers
  const handleViewRegister = (register: any) => {
        setViewingRegister(register);
    setShowViewRegisterModal(true);
  };

  const handleCreateRegister = () => {
    setShowCreateRegisterModal(true);
  };

  const handleEditRegister = (register: any) => {
        setEditingRegister(register);
    setShowEditRegisterModal(true);
  };

  const handleDeleteRegister = async (register: any) => {
    if (window.confirm(t('risk.registers.confirmDelete', { name: register.name }))) {
      try {
        await riskRegisterService.delete(register.id);
        // Table will automatically refresh
      } catch (error) {
        console.error('Error deleting register:', error);
        alert(t('risk.registers.deleteError'));
      }
    }
  };

  const handleCreateRegisterSubmit = async (data: any) => {
    setRegisterLoading(true);
    try {
      await riskRegisterService.create(data);
      setShowCreateRegisterModal(false);
    } catch (error) {
      console.error('Error creating register:', error);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleEditRegisterSubmit = async (data: any) => {
    setRegisterLoading(true);
    try {
      await riskRegisterService.update(editingRegister.id, data);
      setShowEditRegisterModal(false);
      setEditingRegister(null);
    } catch (error) {
      console.error('Error updating register:', error);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCancelRegister = () => {
    setShowCreateRegisterModal(false);
    setShowEditRegisterModal(false);
    setShowViewRegisterModal(false);
    setEditingRegister(null);
    setViewingRegister(null);
  };

  const handleHeaderCreateRisk = () => {
    setActiveTab('pool');
    setAutoOpenCreate(true);
  };

  const handleHeaderRaiseRisk = () => {
    setShowQuickRiskModal(true);
  };

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="p-4">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1">
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">
                {t('risk.management.title')}
              </h1>
              {activeTab === 'overview' && (
                <button
                  type="button"
                  onClick={handleHeaderRaiseRisk}
                  className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('dashboard.raiseRisk.title')}
                </button>
              )}
              {activeTab === 'pool' && (
                <button
                  type="button"
                  onClick={handleHeaderCreateRisk}
                  className="inline-flex items-center justify-center px-4 py-2 bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] text-sm font-medium rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('common.create')}
                </button>
              )}
            </div>
            <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
              {t('risk.management.description')}
            </p>
          </div>

          <div className="mt-6">
        <div className="border-b border-[var(--color-border)]">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'overview'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
              }`}
            >
              {t('risk.overview')}
            </button>
            <button
              onClick={() => setActiveTab('pool')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'pool'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
              }`}
            >
              {t('risk.pool')}
            </button>
            <button
              onClick={() => setActiveTab('registers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'registers'
                  ? 'border-[var(--color-accent-bg)] text-[var(--color-accent-bg)]'
                  : 'border-transparent text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)] hover:border-[var(--color-border)]'
              }`}
            >
              {t('risk.registers')}
            </button>
          </nav>
        </div>
      </div>

      <div className="mt-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left side column - Risk Dashboard and Analytics */}
            <div className="lg:col-span-2 space-y-6">
              <RiskDashboardComponent />
              <RiskAnalytics />
            </div>
            
            {/* Right side column - Heat Map, Recent Activity, and Deadlines */}
            <div className="lg:col-span-1 space-y-6">
              {/* Risk Heat Map */}
              <RiskHeatMap />
              
              {/* Recent Activity - scrollable with infinite scroll */}
              <RiskRecentActivity />
              
              {/* Upcoming Deadlines - scrollable with infinite scroll */}
              <RiskDeadlines />
            </div>
          </div>
        )}

        {activeTab === 'pool' && (
          <RiskPool 
            onBulkAssign={handleBulkAssign} 
            autoOpenCreate={autoOpenCreate}
            onCreateModalClosed={() => setAutoOpenCreate(false)}
          />
        )}

        {activeTab === 'registers' && (
          <FlexibleCRUDTable
            entity={riskRegisterEntity}
            service={riskRegisterService}
            onView={handleViewRegister}
            onEdit={handleEditRegister}
            onDelete={handleDeleteRegister}
            onCreate={handleCreateRegister}
            onOperationComplete={() => {
              // Table will automatically refresh after operations
            }}
            searchPlaceholder={t('risk.registers.searchPlaceholder')}
            showPagination={true}
            showSearch={true}
          />
        )}
      </div>

      {/* Register Modals */}
      {showCreateRegisterModal && (
        <EditModal
          isOpen={showCreateRegisterModal}
          onClose={handleCancelRegister}
          title={`Create ${riskRegisterEntity.displayNameSingular}`}
          entity={riskRegisterEntity}
          onSubmit={handleCreateRegisterSubmit}
          onCancel={handleCancelRegister}
          loading={registerLoading}
        />
      )}

      {showEditRegisterModal && editingRegister && (
        <EditModal
          isOpen={showEditRegisterModal}
          onClose={handleCancelRegister}
          title={`Edit ${riskRegisterEntity.displayNameSingular}`}
          entity={riskRegisterEntity}
          initialData={editingRegister}
          onSubmit={handleEditRegisterSubmit}
          onCancel={handleCancelRegister}
          loading={registerLoading}
        />
      )}

      {showViewRegisterModal && viewingRegister && (
        <ViewModal
          isOpen={showViewRegisterModal}
          onClose={handleCancelRegister}
          title={`View ${riskRegisterEntity.displayNameSingular}`}
          data={viewingRegister}
          fields={riskRegisterEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}

      <QuickRiskCreationModal
        isOpen={showQuickRiskModal}
        onClose={() => setShowQuickRiskModal(false)}
        onRiskCreated={() => {
          setShowQuickRiskModal(false);
        }}
      />
        </div>
      </div>
    </div>
  );
};